import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, PackageOpen, MapPin, ArrowRight, Check, Trash2, Truck, Anchor, Plane, Pencil, Printer, History, Ship, Send, Package, Copy, Tag, MessageCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp, trackMessage } from "@/lib/whatsapp";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { pdfStr, isRTL } from "@/lib/pdfStrings";
import { PdfLangMenu, PdfLangSelect } from "@/components/PdfLangMenu";
import { CountrySelect, ProvinceSelect } from "@/components/GeoSelect";
import { PackageNotes } from "@/components/PackageNotes";

const STATUSES = ["recibido", "despacho", "en_aduana", "en_reparto", "entregado"];
const STATUS_COLORS = {
  recibido: "bg-slate-100 text-slate-700 border-slate-200",
  despacho: "bg-blue-100 text-red-700 border-red-200",
  en_aduana: "bg-amber-100 text-amber-800 border-amber-200",
  en_reparto: "bg-indigo-100 text-indigo-700 border-indigo-200",
  entregado: "bg-emerald-100 text-emerald-700 border-emerald-200",
};
const MODE_ICON = { aereo: Plane, terrestre: Truck, maritimo: Anchor };

const EMPTY_ADDR = {
  nombre_completo: "", empresa: "", email: "", telefono: "", telefono_prefijo: "",
  pais: "", calle: "", numero: "", codigo_postal: "", ciudad: "", provincia: "",
  tax_id: "", referencia: "", alias: "",
};

const EMPTY = {
  tracking_code: "", client_id: "", oficina: "", origen: "", destino: "",
  descripcion: "", peso: 0, cbm: 0, modo: "terrestre",
  estado: "recibido", fecha_estimada: "", notas: "",
  numero_contenedor: "", salida_prevista: "", salida_confirmada: "", llegada_estimada: "",
  origen_addr: { ...EMPTY_ADDR },
  destino_addr: { ...EMPTY_ADDR },
  items: [],
};

export default function Envios() {
  const { api, user } = useAuth();
  const { t, dir, lang } = useI18n();
  const nav = useNavigate();
  const isClient = user.role === "cliente";
  const canManage = user.role === "admin" || user.role === "operaciones" || user.role === "ventas";
  const canUpdateStatus = canManage;

  const [rows, setRows] = useState([]);
  const [myLabels, setMyLabels] = useState([]);
  const [search, setSearch] = useState("");
  const [waAuto, setWaAuto] = useState(() => localStorage.getItem("wa_auto") !== "0");
  const [clients, setClients] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditData, setAuditData] = useState(null);
  // Multi-select for "Assign to Voyage"
  const [selected, setSelected] = useState(new Set());
  const [voyages, setVoyages] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ voyage_id: "", nombre: "", modo: "terrestre", destino: "", container_number: "", vehiculo: "", conductor: "", notas: "", report_lang: "en" });
  const [assignBusy, setAssignBusy] = useState(false);
  // Labels → boxes picker (tracking codes come from real printed labels)
  const [availableLabels, setAvailableLabels] = useState([]);
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [labelSearch, setLabelSearch] = useState("");
  const [labelSel, setLabelSel] = useState(new Set());

  const loadLabels = () => api.get("/labels", { params: { estado: "disponible" } })
    .then(r => setAvailableLabels(r.data || [])).catch(()=>{});

  const addSelectedLabels = () => {
    const picked = availableLabels.filter(l => labelSel.has(l.codigo));
    setForm(f => ({
      ...f,
      items: [
        ...f.items,
        ...picked.map(l => ({
          code: l.codigo,
          descripcion: l.contenido || l.referencia || "",
          peso: +l.peso_total || 0,
          cbm: +l.cbm || 0,
          bultos: Math.max(1, +l.numero_paquetes || 1),
        })),
      ],
    }));
    setLabelSel(new Set());
    setLabelPickerOpen(false);
  };

  const canDispatch = user.role === "admin" || user.role === "operaciones" || user.role === "almacen";

  const isReady = (s) => !s.voyage_id && !s.manifest_id && s.estado !== "entregado";
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());

  const openAudit = async (s) => {
    setAuditFor(s);
    setAuditData(null);
    try {
      const { data } = await api.get(`/shipments/${s.id}/audit`);
      setAuditData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const load = () => api.get("/shipments").then(r => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/countries").then(r => setCountries(r.data.filter(c => c.activo))).catch(()=>{});
    if (canManage) {
      api.get("/clientes").then(r => setClients(r.data)).catch(()=>{});
      api.get("/oficinas").then(r => setOficinas(r.data.oficinas || [])).catch(()=>{});
      loadLabels();
    }
    if (isClient) {
      // Client's own registered packages (labels) — traceable via /track/{code}
      api.get("/labels").then(r => setMyLabels(r.data || [])).catch(() => {});
    }
    if (canDispatch) {
      api.get("/voyages").then(r => setVoyages((r.data || []).filter(v => v.activa !== false))).catch(()=>{});
    }
  // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Auto-fill origen/destino display strings from address ciudad/pais
      const payload = { ...form };
      const oa = payload.origen_addr || {};
      const da = payload.destino_addr || {};
      if (!payload.origen && (oa.ciudad || oa.pais)) {
        payload.origen = [oa.ciudad, oa.pais].filter(Boolean).join(", ");
      }
      if (!payload.destino && (da.ciudad || da.pais)) {
        payload.destino = [da.ciudad, da.pais].filter(Boolean).join(", ");
      }
      if (editingId) {
        // Editing: PATCH only the mutable fields
        const { tracking_code, client_id, historial, id, created_at, updated_at, cliente_nombre, cliente_email, ...update } = payload;
        await api.patch(`/shipments/${editingId}`, update);
        toast.success(t("saved"));
      } else {
        await api.post("/shipments", payload);
        toast.success(t("shipment_created"));
      }
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
      if (canManage) loadLabels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openEdit = (s) => {
    setForm({
      ...EMPTY,
      ...s,
      origen_addr: { ...EMPTY_ADDR, ...(s.origen_addr || {}) },
      destino_addr: { ...EMPTY_ADDR, ...(s.destino_addr || {}) },
    });
    setEditingId(s.id);
    setShowForm(true);
  };

  const openNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setShowForm(true);
  };

  const notifyWhatsApp = (s, estado) => {
    const tel = s.cliente_telefono || s.receptor?.telefono || s.remitente?.telefono || "";
    if (!tel) { toast.error(t("phone") + " —"); return; }
    openWhatsApp(tel, trackMessage({
      nombre: s.cliente_nombre,
      tracking: s.tracking_code,
      estado: t(`status_${estado}`),
      url: `${window.location.origin}/track/${s.tracking_code || ""}`,
    }));
  };

  const changeStatus = async (s, estado) => {
    try {
      await api.patch(`/shipments/${s.id}/status`, { estado, nota: "" });
      load();
      const tel = s.cliente_telefono || s.receptor?.telefono || s.remitente?.telefono || "";
      if (waAuto && tel) {
        // Open WhatsApp from a real user gesture (toast tap) so the browser doesn't block the popup
        toast.success(t("saved"), {
          action: { label: t("whatsapp_notify"), onClick: () => notifyWhatsApp({ ...s, estado }, estado) },
          duration: 9000,
        });
      } else {
        toast.success(t("saved"));
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleWaAuto = () => {
    setWaAuto(v => { localStorage.setItem("wa_auto", v ? "0" : "1"); return !v; });
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/shipments/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const genCode = () => {
    const now = new Date();
    setForm(f => ({ ...f, tracking_code: `MEOS-${now.getFullYear()}-${String(Date.now()).slice(-6)}` }));
  };

  const printLabel = async (s, lang = "en", item = null) => {
    // A6 label 105x148 mm ≈ 297x420 pt. Localized via pdfStr(lang).
    const S = pdfStr(lang);
    const rtl = isRTL(lang);
    const doc = new jsPDF({ unit: "pt", format: [297, 420] });
    const w = doc.internal.pageSize.getWidth();
    const oa = s.origen_addr || {};
    const da = s.destino_addr || {};
    // Register Amiri if any text on this label may contain Arabic
    const anyArabic = rtl || [s.tracking_code, s.descripcion, oa.nombre_completo, oa.ciudad, oa.pais, da.nombre_completo, da.ciudad, da.pais, item?.descripcion].some(hasArabic);
    if (anyArabic) await ensureArabicFont(doc);

    // Effective tracking (item overrides master when printing one specific box)
    const displayCode = item?.code || s.tracking_code;
    const displayDesc = item?.descripcion || s.descripcion;
    const displayWeight = item?.peso ?? s.peso;
    const displayCbm = item?.cbm ?? s.cbm;

    // Generate real Code128 barcode via JsBarcode -> canvas -> dataURL
    let barcodeDataUrl = null;
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, displayCode, { format: "CODE128", displayValue: false, height: 60, margin: 0, width: 2 });
      barcodeDataUrl = canvas.toDataURL("image/png");
    } catch (e) { console.warn("Label barcode failed:", e); }

    const img = new Image();
    img.onload = () => { try { doc.addImage(img, "PNG", 12, 10, 55, 36); } catch (e) { console.warn("Label logo embed failed:", e); } render(); };
    img.onerror = render;
    img.src = "/logo.png";

    function render() {
      // Header
      doc.setFont("helvetica","bold"); doc.setFontSize(11); doc.setTextColor(185,28,28);
      doc.text("MEOS CARGO", w - 12, 25, { align: "right" });
      doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(100,116,139);
      pdfText(doc, S.label_title, w - 12, 36, { align: "right" });
      const modeKey = { aereo: "mode_aereo", terrestre: "mode_terrestre", maritimo: "mode_maritimo" };
      const modeLabel = S[modeKey[s.modo]] || (s.modo || "").toUpperCase();
      doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(80,80,80);
      pdfText(doc, modeLabel, w - 12, 46, { align: "right" });

      // Tracking box
      doc.setDrawColor(0); doc.setLineWidth(1);
      doc.rect(12, 55, w - 24, 62);
      doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(120,120,120);
      pdfText(doc, S.tracking.toUpperCase(), 18, 68);
      doc.setFontSize(14); doc.setTextColor(0);
      doc.text(displayCode, 18, 84);
      // Item badge if printing a sub-box
      if (item) {
        doc.setFillColor(15, 23, 42); doc.rect(w - 82, 60, 68, 14, "F");
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(255,255,255);
        pdfText(doc, `${S.box.toUpperCase()} · ${displayCode.split("-").slice(-1)[0]}`, w - 48, 70, { align: "center" });
      }

      // Real barcode (Code128)
      if (barcodeDataUrl) {
        try { doc.addImage(barcodeDataUrl, "PNG", 18, 88, w - 44, 24); } catch (e) { console.warn("addImage barcode failed:", e); }
      }

      // Helper to draw address block (localized)
      const drawAddr = (title, addr, y, accentColor) => {
        doc.setFillColor(...accentColor);
        doc.rect(12, y, w - 24, 12, "F");
        doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(255,255,255);
        pdfText(doc, title, 16, y + 8);
        if (addr.pais) {
          doc.setFontSize(7);
          pdfText(doc, String(addr.pais).toUpperCase(), w - 16, y + 8, { align: "right" });
        }
        doc.setDrawColor(200); doc.setLineWidth(0.5);
        doc.rect(12, y + 12, w - 24, 88);
        doc.setTextColor(0);
        let ly = y + 24;
        doc.setFont("helvetica","bold"); doc.setFontSize(9);
        const name = [addr.nombre_completo, addr.empresa && `(${addr.empresa})`].filter(Boolean).join(" ");
        pdfText(doc, (name || "—").substring(0, 55), 16, ly); ly += 11;
        doc.setFont("helvetica","normal"); doc.setFontSize(8);
        const line1 = [addr.calle, addr.numero].filter(Boolean).join(" ");
        if (line1) { pdfText(doc, line1.substring(0, 60), 16, ly); ly += 10; }
        const line2 = [addr.codigo_postal, addr.ciudad].filter(Boolean).join(" · ");
        if (line2) { pdfText(doc, line2.substring(0, 60), 16, ly); ly += 10; }
        const line3 = [addr.provincia, addr.pais].filter(Boolean).join(", ");
        if (line3) { pdfText(doc, line3.substring(0, 60), 16, ly); ly += 10; }
        if (addr.telefono) {
          const phone = [addr.telefono_prefijo, addr.telefono].filter(Boolean).join(" ");
          pdfText(doc, `${S.phone}: ${phone}`, 16, ly); ly += 10;
        }
        if (addr.email) { doc.text(addr.email.substring(0, 45), 16, ly); ly += 10; }
        if (addr.tax_id) {
          doc.setFontSize(7); doc.setTextColor(120,120,120);
          pdfText(doc, `${S.tax_id}: ${addr.tax_id}`, 16, ly);
          doc.setTextColor(0);
        }
      };

      drawAddr(`${S.from} / ${S.sender.toUpperCase()}`, oa, 128, [37, 99, 235]);
      drawAddr(`${S.to} / ${S.receiver.toUpperCase()}`, da, 234, [185, 28, 28]);

      // Package info footer
      doc.setDrawColor(0); doc.setLineWidth(0.5);
      doc.rect(12, 340, w - 24, 42);
      doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(120,120,120);
      pdfText(doc, S.weight.toUpperCase(), 20, 352);
      pdfText(doc, S.cbm.toUpperCase(), 90, 352);
      pdfText(doc, S.mode.toUpperCase(), 160, 352);
      pdfText(doc, S.container.toUpperCase(), 220, 352);
      doc.setFont("helvetica","normal"); doc.setFontSize(10); doc.setTextColor(0);
      doc.text(String(displayWeight || 0) + " kg", 20, 368);
      doc.text(String(displayCbm || 0), 90, 368);
      pdfText(doc, modeLabel, 160, 368);
      doc.text(s.numero_contenedor || "—", 220, 368);

      if (displayDesc) {
        doc.setFontSize(7); doc.setTextColor(60,60,60);
        pdfText(doc, `${S.contents}: ${String(displayDesc).substring(0, 90)}`, 20, 378);
      }

      // Item counter (if part of a multi-box shipment and printing an item)
      if (item && (s.items || []).length > 1) {
        const idx = (s.items || []).findIndex(i => i.code === item.code) + 1;
        doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(30, 41, 59);
        doc.text(`${idx}/${s.items.length}`, w - 30, 378, { align: "right" });
      }

      doc.setFontSize(6); doc.setTextColor(150,150,150);
      pdfText(doc, `${S.track_at} /track/${s.tracking_code}  ·  ${S.footer_tagline}`, w/2, 405, { align: "center" });

      doc.save(`Label_${displayCode}.pdf`);
      toast.success("PDF");
    }
  };

  // Print labels for ALL items of a master shipment into a single multipage PDF.
  const printAllItemLabels = async (s, lang = "en") => {
    const items = s.items || [];
    if (items.length === 0) return printLabel(s, lang);
    // Sequentially print each item; jsPDF instances are per-doc so we do it serial.
    for (const it of items) {
      // eslint-disable-next-line no-await-in-loop
      await printLabel(s, lang, it);
    }
    toast.success(`${items.length} PDF`);
  };

  // Open the assign-to-voyage modal for selected shipments
  const openAssign = () => {
    if (selected.size === 0) return toast.error(t("select_shipments_hint"));
    // Prefill: infer mode from first selected shipment
    const first = rows.find(r => selected.has(r.id));
    setAssignForm({
      voyage_id: "",
      nombre: `${t("dispatch")} ${new Date().toISOString().slice(0, 10)}`,
      modo: first?.modo || "terrestre",
      destino: first?.destino || "",
      container_number: "",
      vehiculo: "",
      conductor: "",
      notas: "",
      report_lang: "en",
    });
    setAssignOpen(true);
  };

  // When user picks a voyage, autofill destino/modo/container from it
  const pickVoyage = (vid) => {
    const v = voyages.find(x => x.id === vid);
    setAssignForm(f => ({
      ...f,
      voyage_id: vid,
      modo: v?.modo || f.modo,
      destino: v?.puerto_destino || f.destino,
      container_number: v?.container_number || f.container_number,
    }));
  };

  // Submit assignment: calls /manifests/auto-exit with the selected tracking_codes,
  // then downloads a compact "Manifest Copy" PDF for the operator.
  const submitAssign = async (e) => {
    e.preventDefault();
    const trackingCodes = rows.filter(r => selected.has(r.id)).map(r => r.tracking_code).filter(Boolean);
    if (trackingCodes.length === 0) return toast.error(t("select_shipments_hint"));
    setAssignBusy(true);
    try {
      const { data } = await api.post("/manifests/auto-exit", {
        nombre: assignForm.nombre || `Salida ${new Date().toISOString().slice(0, 10)}`,
        modo: assignForm.modo || "terrestre",
        destino: assignForm.destino || "",
        voyage_id: assignForm.voyage_id || "",
        container_number: assignForm.container_number || "",
        vehiculo: assignForm.vehiculo || "",
        conductor: assignForm.conductor || "",
        notas: assignForm.notas || "",
        tracking_codes: trackingCodes,
      });
      const notFound = data?.not_found || [];
      if (notFound.length > 0) {
        toast.warning(`${t("assigned")} ${data.packages_included || 0}. ${t("not_found")}: ${notFound.join(", ")}`);
      } else {
        toast.success(`${t("assigned")} ${data.packages_included || 0} ${t("shipments").toLowerCase()}`);
      }
      // Fetch full manifest with populated shipments for the copy
      let manifestDoc = data.manifest;
      try {
        const { data: full } = await api.get(`/manifests/${data.manifest.id}`);
        if (full) manifestDoc = full;
      } catch (err) {
        // Non-fatal: we already have a manifestDoc from the assign response
        console.warn("Manifest detail fetch failed, using assign response:", err?.response?.status || err?.message);
      }
      await printManifestCopy(manifestDoc, assignForm.report_lang || "en");
      clearSelected();
      setAssignOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setAssignBusy(false); }
  };

  // Compact "Manifest Copy" PDF — quick operator receipt for the dispatch
  const printManifestCopy = async (m, lang = "en") => {
    const S = pdfStr(lang);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    const anyArabic = isRTL(lang) || [m.nombre, m.origen, m.destino, m.vehiculo, m.conductor, m.notas]
      .concat((m.shipments || []).map(s => `${s.tracking_code} ${s.origen} ${s.destino}`))
      .some(hasArabic);
    if (anyArabic) await ensureArabicFont(doc);

    // Barcode
    let barcodeDataUrl = null;
    const barcodeValue = (m.manifest_barcode || m.codigo || "").trim();
    if (barcodeValue) {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, barcodeValue, { format: "CODE128", displayValue: false, height: 45, margin: 0, width: 1.6 });
        barcodeDataUrl = canvas.toDataURL("image/png");
      } catch (e) { console.warn("Manifest copy barcode failed:", e); }
    }

    // Header black band
    doc.setFillColor(15, 23, 42); doc.rect(0, 0, w, 54, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(255,255,255);
    doc.text("MEOS CARGO", w - 40, 30, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(203, 213, 225);
    pdfText(doc, S.manifest_copy.toUpperCase(), w - 40, 46, { align: "right" });

    // Red service band
    const modeColor = m.modo === "aereo" ? [30, 64, 175] : m.modo === "maritimo" ? [8, 145, 178] : [185, 28, 28];
    doc.setFillColor(...modeColor); doc.rect(0, 54, w, 22, "F");
    const modeKey = { aereo: "mode_aereo", terrestre: "mode_terrestre", maritimo: "mode_maritimo" };
    const modeLabelPdf = S[modeKey[m.modo]] || (m.modo || "").toUpperCase();
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(255, 255, 255);
    pdfText(doc, modeLabelPdf, 40, 70);
    doc.text(new Date().toLocaleString(), w - 40, 70, { align: "right" });

    // Manifest code + barcode
    let y = 100;
    doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(15, 23, 42);
    pdfText(doc, m.codigo || "-", 40, y);
    y += 18;
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(71, 85, 105);
    pdfText(doc, m.nombre || "", 40, y);

    if (barcodeDataUrl) {
      try {
        doc.addImage(barcodeDataUrl, "PNG", w - 220, 90, 180, 45);
        doc.setFont("courier", "bold"); doc.setFontSize(8); doc.setTextColor(0, 0, 0);
        doc.text(barcodeValue, w - 130, 145, { align: "center" });
      } catch (e) { console.warn("Manifest copy barcode embed failed:", e); }
    }

    // Meta grid
    y += 30;
    doc.setFillColor(248, 250, 252); doc.rect(40, y, w - 80, 60, "F");
    doc.setDrawColor(226, 232, 240); doc.rect(40, y, w - 80, 60);
    doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
    const col = (w - 80) / 4;
    pdfText(doc, S.origin.toUpperCase(), 50, y + 14);
    pdfText(doc, S.destination.toUpperCase(), 50 + col, y + 14);
    pdfText(doc, S.vehicle.toUpperCase(), 50 + col * 2, y + 14);
    pdfText(doc, S.driver.toUpperCase(), 50 + col * 3, y + 14);
    doc.setFontSize(11); doc.setTextColor(15, 23, 42);
    pdfText(doc, m.origen || "-", 50, y + 32);
    pdfText(doc, m.destino || "-", 50 + col, y + 32);
    pdfText(doc, m.vehiculo || "-", 50 + col * 2, y + 32);
    pdfText(doc, m.conductor || "-", 50 + col * 3, y + 32);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
    pdfText(doc, m.fecha_salida ? `${S.departure}: ${m.fecha_salida}` : "", 50, y + 50);
    y += 70;

    // Shipments table
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(15, 23, 42);
    pdfText(doc, `${S.shipments.toUpperCase()} (${(m.shipments || m.shipment_ids || []).length})`, 40, y);
    y += 6;
    doc.setFillColor(15, 23, 42); doc.rect(40, y, w - 80, 18, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
    doc.text("#", 46, y + 12);
    pdfText(doc, S.tracking_code.toUpperCase(), 68, y + 12);
    pdfText(doc, S.route.toUpperCase(), 220, y + 12);
    pdfText(doc, S.weight_kg.toUpperCase(), w - 130, y + 12);
    y += 20;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(30, 41, 59);
    const list = m.shipments || [];
    if (list.length === 0) {
      // Fallback: use tracking codes from the current selection
      const codes = rows.filter(r => selected.has(r.id));
      codes.forEach((s, i) => renderRow(s, i));
    } else {
      list.forEach((s, i) => renderRow(s, i));
    }
    function renderRow(s, i) {
      if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(40, y - 12, w - 80, 16, "F"); }
      if (y > 780) { doc.addPage(); y = 60; }
      doc.setTextColor(30, 41, 59);
      doc.text(String(i + 1), 46, y);
      doc.setFont("courier", "bold");
      doc.text((s.tracking_code || "").slice(0, 22), 68, y);
      doc.setFont("helvetica", "normal");
      pdfText(doc, `${s.origen || ""} → ${s.destino || ""}`, 220, y);
      doc.text(String(s.peso || 0), w - 130, y);
      y += 16;
    }

    // Not-found alert
    const notFound = m.not_found_codes || [];
    if (notFound.length > 0) {
      y += 10;
      doc.setFillColor(254, 226, 226); doc.rect(40, y, w - 80, 30, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(153, 27, 27);
      pdfText(doc, `${S.not_found}: ${notFound.join(", ")}`, 50, y + 18);
      y += 34;
    }

    // Footer
    doc.setDrawColor(226, 232, 240); doc.line(40, 800, w - 40, 800);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    pdfText(doc, S.footer_tagline, w / 2, 815, { align: "center" });

    doc.save(`Manifest_${m.codigo || "copy"}.pdf`);
    toast.success("PDF");
  };

  // Search filter runs on every keystroke; memoize to skip work when the
  // shipments list or query hasn't changed.
  const q = search.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!q) return rows;
    return rows.filter(s => {
      const hay = [
        s.tracking_code, s.cliente_nombre, s.cliente_email, s.origen, s.destino,
        s.oficina, s.estado, s.numero_contenedor, s.voyage?.codigo,
        ...(s.items || []).map(i => `${i.code} ${i.descripcion || ""}`),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q) || t(`status_${s.estado}`).toLowerCase().includes(q);
    });
  }, [rows, q, t]);

  // Memoise the filter chain that feeds the "available labels" picker inside
  // the shipment modal. Recomputing on every keystroke of an unrelated input
  // would iterate over hundreds of rows uselessly.
  const availableLabelsFiltered = useMemo(() => {
    const clientId = form.client_id;
    const oficina = form.oficina;
    const pais = (form.origen_addr?.pais || "").trim().toLowerCase();
    const s = labelSearch.trim().toLowerCase();
    const usedCodes = new Set((form.items || []).map(i => i.code));
    return availableLabels.filter(l => {
      if (clientId && l.client_id && l.client_id !== clientId) return false;
      if (oficina && l.oficina && l.oficina !== oficina) return false;
      const lp = (l.origen_pais || "").trim().toLowerCase();
      if (pais && lp && lp !== pais) return false;
      if (usedCodes.has(l.codigo)) return false;
      if (s) {
        const hay = [l.codigo, l.receptor_nombre, l.remitente_nombre, l.contenido, l.referencia]
          .filter(Boolean).map(v => String(v).toLowerCase()).join(" ");
        if (!hay.includes(s)) return false;
      }
      return true;
    }).slice(0, 60);
  }, [availableLabels, form.client_id, form.oficina, form.origen_addr, form.items, labelSearch]);

  // Memoise the box totals (Σ peso · Σ CBM · Σ bultos) shown in the shipment
  // modal — this runs 3× per render otherwise.
  const itemTotals = useMemo(() => {
    const items = form.items || [];
    let peso = 0, cbm = 0, bultos = 0;
    for (const i of items) {
      const bu = Math.max(1, +i.bultos || 1);
      peso += (+i.peso || 0) * bu;
      cbm  += (+i.cbm  || 0) * bu;
      bultos += bu;
    }
    return { peso: peso.toFixed(2), cbm: cbm.toFixed(3), bultos };
  }, [form.items]);

  // Memoize the "selected shipments" list rendered inside the batch-dispatch
  // modal so repeated renders of parent state don't re-run the filter.
  const selectedRows = useMemo(() => rows.filter(r => selected.has(r.id)), [rows, selected]);

  return (
    <div className="space-y-4" data-testid="envios-page">
      {user.role !== "cliente" && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 text-xs text-amber-900" data-testid="legacy-notice">
          {t("legacy_notice")}
        </div>
      )}
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">
            {isClient ? t("my_shipments") : t("all_shipments")}
          </h2>
          <p className="text-sm text-slate-500">{filteredRows.length} / {rows.length} {t("shipments").toLowerCase()}</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            {!isClient && (
              <button
                data-testid="wa-auto-toggle"
                onClick={toggleWaAuto}
                title={t("whatsapp_auto_hint")}
                className={`px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5 border transition-colors ${waAuto ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-300"}`}
              >
                <MessageCircle className="w-3.5 h-3.5" /> {t("whatsapp_auto")} {waAuto ? "✓" : ""}
              </button>
            )}
            {canDispatch && selected.size > 0 && (
              <button
                data-testid="assign-voyage-btn"
                onClick={openAssign}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" /> {t("assign_to_voyage")} ({selected.size})
              </button>
            )}
            <button
              data-testid="new-shipment-btn"
              onClick={openNew}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t("new_shipment")}
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
        <input
          data-testid="ship-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search_shipment")}
          className="w-full ps-9 pe-9 py-2.5 border border-slate-300 rounded-xl text-sm outline-none focus:border-red-600 bg-white"
        />
        {search && (
          <button data-testid="ship-search-clear" onClick={() => setSearch("")} className="absolute top-1/2 -translate-y-1/2 end-3 text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isClient && myLabels.length > 0 && (
        <ClientLabelsSection labels={myLabels} t={t} search={search} nav={nav} />
      )}

      {rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400">
          <PackageOpen className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <p>{isClient ? t("no_shipments") : t("no_shipments_admin")}</p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400" data-testid="ship-no-results">
          <Search className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <p>{t("no_search_results")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRows.map(s => {
            const Icon = MODE_ICON[s.modo] || Truck;
            const isOpen = expanded === s.id;
            const ready = isReady(s);
            const isSel = selected.has(s.id);
            return (
              <div key={s.id} data-testid={`ship-${s.tracking_code}`} className={`bg-white border rounded-xl overflow-hidden ${isSel ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200"}`}>
                <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {canDispatch && ready && (
                      <input
                        type="checkbox"
                        data-testid={`sel-ship-${s.id}`}
                        checked={isSel}
                        onChange={()=>toggleSelect(s.id)}
                        className="w-4 h-4 accent-indigo-600 shrink-0"
                        title={t("select_for_dispatch")}
                      />
                    )}
                    <div className="w-11 h-11 shrink-0 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold text-slate-900">{s.tracking_code}</span>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLORS[s.estado]}`}>
                          {t(`status_${s.estado}`)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap">
                        <MapPin className="w-3 h-3 text-slate-400" /> {s.origen}
                        <ArrowRight className={`w-3 h-3 text-slate-400 ${dir === "rtl" ? "rtl-flip" : ""}`} />
                        {s.destino}
                        {!isClient && s.cliente_nombre && (
                          <span className="text-xs text-slate-400 ms-2">· {s.cliente_nombre}</span>
                        )}
                        {!isClient && s.oficina && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 ms-1">{s.oficina}</span>
                        )}
                        {s.voyage && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 ms-1 flex items-center gap-1"
                            data-testid={`ship-voy-${s.id}`}
                            title={`${s.voyage.puerto_origen} → ${s.voyage.puerto_destino}${s.voyage.salida ? ' · ' + s.voyage.salida : ''}`}
                          >
                            <Ship className="w-3 h-3" /> {s.voyage.codigo}
                          </span>
                        )}
                        {s.container_number && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 ms-1">{s.container_number}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                      className="text-xs font-semibold text-red-600 hover:underline"
                    >
                      {isOpen ? "▲" : "▼"} {t("view_details")}
                    </button>
                    <PdfLangMenu
                      testid={`print-ship-${s.id}`}
                      compact
                      icon={<Printer className="w-4 h-4" />}
                      onPrint={(lg) => (s.items || []).length > 1 ? printAllItemLabels(s, lg) : printLabel(s, lg)}
                    />
                    {canManage && (
                      <button
                        data-testid={`audit-ship-${s.id}`}
                        onClick={() => openAudit(s)}
                        className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-md"
                        title={t("audit_trail")}
                      >
                        <History className="w-4 h-4" />
                      </button>
                    )}
                    {canManage && (
                      <button
                        data-testid={`edit-ship-${s.id}`}
                        onClick={() => openEdit(s)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title={t("edit")}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {user.role === "admin" && (
                      <button onClick={() => remove(s.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-4">
                    {/* Sender / Receiver address cards */}
                    {(s.origen_addr || s.destino_addr) && (
                      <div className="grid md:grid-cols-2 gap-3">
                        <AddressCard side="origen" addr={s.origen_addr || {}} t={t} />
                        <AddressCard side="destino" addr={s.destino_addr || {}} t={t} />
                      </div>
                    )}
                    {s.descripcion && <p className="text-sm text-slate-700"><b>{t("description")}:</b> {s.descripcion}</p>}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-xs text-slate-500 uppercase tracking-wider">{t("weight_kg")}</span><div className="font-semibold tabular-nums">{s.peso} kg</div></div>
                      <div><span className="text-xs text-slate-500 uppercase tracking-wider">CBM</span><div className="font-semibold tabular-nums">{s.cbm}</div></div>
                      <div><span className="text-xs text-slate-500 uppercase tracking-wider">{t("mode")}</span><div className="font-semibold">{t(s.modo)}</div></div>
                      <div><span className="text-xs text-slate-500 uppercase tracking-wider">{t("estimated_date")}</span><div className="font-semibold">{s.fecha_estimada || "—"}</div></div>
                    </div>

                    {/* Master-shipment items (individual boxes) */}
                    {(s.items && s.items.length > 0) && (
                      <div className="border border-slate-200 rounded-lg bg-white p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-slate-500" />
                            <p className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                              {t("boxes")} · {s.items.length}
                            </p>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold tabular-nums">
                              Σ {s.bultos_total || s.items.length} {t("bultos_short")}
                            </span>
                          </div>
                          <PdfLangMenu
                            testid={`print-all-${s.id}`}
                            label={t("print_all_labels")}
                            icon={<Printer className="w-3 h-3" />}
                            onPrint={(lg) => printAllItemLabels(s, lg)}
                          />
                        </div>
                        <div className="divide-y divide-slate-100">
                          {s.items.map((it, idx) => (
                            <div key={it.id || it.code} className="py-2 flex items-center gap-3" data-testid={`item-row-${it.code}`}>
                              <div className="w-6 h-6 rounded bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-xs font-bold text-slate-800">{it.code}</div>
                                <div className="text-[11px] text-slate-500 truncate">{it.descripcion || "—"}</div>
                              </div>
                              <div className="text-xs tabular-nums text-slate-600 whitespace-nowrap">
                                {it.peso} kg · {it.cbm} m³{it.bultos > 1 ? ` × ${it.bultos}` : ""}
                              </div>
                              <PdfLangMenu
                                testid={`print-item-${it.code}`}
                                compact
                                icon={<Printer className="w-3 h-3" />}
                                onPrint={(lg) => printLabel(s, lg, it)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Timeline */}
                    <div>
                      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">{t("status_history")}</p>
                      <div className="grid grid-cols-5 gap-1">
                        {STATUSES.map((st, i) => {
                          const idx = STATUSES.indexOf(s.estado);
                          const done = i <= idx;
                          return (
                            <div key={st} className="flex flex-col items-center gap-1">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                done ? "bg-blue-600 text-white" : "bg-white border border-slate-300 text-slate-400"
                              }`}>
                                {done ? <Check className="w-4 h-4" /> : i + 1}
                              </div>
                              <span className={`text-[10px] uppercase tracking-wider text-center ${done ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
                                {t(`status_${st}`)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {canManage && (
                      <div className="pt-2 border-t border-slate-200">
                        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("update_status")}</p>
                        <div className="flex flex-wrap gap-2">
                          {STATUSES.map(st => (
                            <button
                              key={st}
                              data-testid={`status-${s.id}-${st}`}
                              onClick={() => changeStatus(s, st)}
                              disabled={st === s.estado}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider border transition-colors ${
                                st === s.estado
                                  ? "bg-slate-200 text-slate-500 border-slate-200 cursor-default"
                                  : "bg-white hover:bg-blue-600 hover:text-white border-slate-300"
                              }`}
                            >
                              {t(`status_${st}`)}
                            </button>
                          ))}
                        </div>
                        <button
                          data-testid={`ship-wa-${s.id}`}
                          onClick={() => notifyWhatsApp(s, s.estado)}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> {t("whatsapp_notify")}
                        </button>
                      </div>
                    )}

                    {s.historial && s.historial.length > 0 && (
                      <div className="pt-2 border-t border-slate-200">
                        <ul className="space-y-1.5 text-xs">
                          {s.historial.slice().reverse().map((h, i) => (
                            <li key={`${h.fecha}-${h.estado}-${i}`} className="flex items-start gap-2">
                              <span className={`inline-block px-1.5 py-0.5 rounded ${STATUS_COLORS[h.estado]} text-[10px] uppercase font-semibold`}>{t(`status_${h.estado}`)}</span>
                              <span className="text-slate-500 tabular-nums">{new Date(h.fecha).toLocaleString()}</span>
                              {h.nota && <span className="text-slate-600">— {h.nota}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Create */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ship-modal">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">{editingId ? t("edit") + " " + t("shipments").toLowerCase() : t("new_shipment")}</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("tracking_code")}</label>
              <div className="flex gap-2">
                <input required data-testid="sf-code" disabled={!!editingId} value={form.tracking_code} onChange={(e)=>setForm({...form, tracking_code: e.target.value})} placeholder={t("tracking_placeholder")} className="input flex-1 disabled:bg-slate-100 disabled:text-slate-500" />
                {!editingId && <button type="button" onClick={genCode} className="px-3 py-2 border border-slate-300 rounded-lg text-xs font-semibold hover:bg-slate-50">{t("auto_prefix")}</button>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("assign_client")} <span className="text-slate-400 font-normal">({t("optional")} · grupaje)</span></label>
              <select data-testid="sf-client" disabled={!!editingId} value={form.client_id} onChange={(e)=>setForm({...form, client_id: e.target.value})} className="input disabled:bg-slate-100 disabled:text-slate-500">
                <option value="">{t("select_option")}</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.fullname} — {c.email}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("assigned_office")}</label>
              <select data-testid="sf-oficina" value={form.oficina} onChange={(e)=>setForm({...form, oficina: e.target.value})} className="input">
                <option value="">— (sin oficina)</option>
                {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Origen / Destino con dirección completa */}
            <AddressBlock
              side="origen"
              addr={form.origen_addr}
              countries={countries}
              onChange={(a) => setForm({ ...form, origen_addr: a })}
              t={t}
              lang={lang}
            />
            <AddressBlock
              side="destino"
              addr={form.destino_addr}
              countries={countries}
              onChange={(a) => setForm({ ...form, destino_addr: a })}
              t={t}
              lang={lang}
            />

            {/* Legacy origen/destino - hidden fallback, auto-filled from address if empty */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("origin")} <span className="text-slate-400 font-normal">({t("optional")})</span></label>
                <input data-testid="sf-origen" value={form.origen} onChange={(e)=>setForm({...form, origen: e.target.value})} placeholder={t("auto_by_province")} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("destination")} <span className="text-slate-400 font-normal">({t("optional")})</span></label>
                <input data-testid="sf-destino" value={form.destino} onChange={(e)=>setForm({...form, destino: e.target.value})} placeholder={t("auto_by_province")} className="input" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("description")}</label>
              <input value={form.descripcion} onChange={(e)=>setForm({...form, descripcion: e.target.value})} className="input" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("mode")}</label>
                <select value={form.modo} onChange={(e)=>setForm({...form, modo: e.target.value})} className="input">
                  <option value="aereo">{t("aereo")}</option>
                  <option value="terrestre">{t("terrestre")}</option>
                  <option value="maritimo">{t("maritimo")}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("weight_kg")}</label>
                <input type="number" step="0.1" value={form.peso} onChange={(e)=>setForm({...form, peso: +e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">CBM</label>
                <input type="number" step="0.01" value={form.cbm} onChange={(e)=>setForm({...form, cbm: +e.target.value})} className="input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("estimated_date")}</label>
                <input type="date" value={form.fecha_estimada} onChange={(e)=>setForm({...form, fecha_estimada: e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("status")}</label>
                <select value={form.estado} onChange={(e)=>setForm({...form, estado: e.target.value})} className="input">
                  {STATUSES.map(st => <option key={st} value={st}>{t(`status_${st}`)}</option>)}
                </select>
              </div>
            </div>

            {/* Container & schedule fields (visible in form; only admin can modify from Schedules page too) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("container_number")}</label>
                <input data-testid="sf-container" value={form.numero_contenedor} onChange={(e)=>setForm({...form, numero_contenedor: e.target.value})} placeholder="MSKU1234567" className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("estimated_departure")}</label>
                <input type="date" data-testid="sf-etd" value={form.salida_prevista} onChange={(e)=>setForm({...form, salida_prevista: e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("confirmed_departure")}</label>
                <input type="date" data-testid="sf-atd" value={form.salida_confirmada} onChange={(e)=>setForm({...form, salida_confirmada: e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("estimated_arrival")}</label>
                <input type="date" value={form.llegada_estimada} onChange={(e)=>setForm({...form, llegada_estimada: e.target.value})} className="input" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("internal_notes")}</label>
              <textarea rows="2" value={form.notas} onChange={(e)=>setForm({...form, notas: e.target.value})} className="input" />
            </div>

            {/* Multi-item editor (master shipment with N boxes) */}
            {!editingId && (
              <div className="border-2 border-blue-200 rounded-lg p-3 bg-blue-50/40" data-testid="items-editor">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                      <Package className="w-4 h-4" /> {t("boxes")} {form.items.length > 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-600 text-white">{form.items.length}</span>}
                    </p>
                    <p className="text-[11px] text-blue-800">{t("boxes_from_labels_hint")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="pick-labels-btn"
                      onClick={()=>{ setLabelPickerOpen(o => !o); setLabelSel(new Set()); }}
                      className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"
                    >
                      <Tag className="w-3 h-3" /> {t("add_from_labels")}
                    </button>
                    <button
                      type="button"
                      data-testid="add-item-btn"
                      onClick={()=>setForm(f => ({ ...f, items: [...f.items, { code: "", descripcion: "", peso: 0, cbm: 0, bultos: 1 }] }))}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> {t("manual_code")}
                    </button>
                  </div>
                </div>

                {labelPickerOpen && (
                  <div className="mb-3 rounded-lg border border-slate-300 bg-white p-3" data-testid="label-picker">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <input
                        data-testid="label-search"
                        value={labelSearch}
                        onChange={(e)=>setLabelSearch(e.target.value)}
                        placeholder={t("search_code")}
                        className="input flex-1"
                      />
                      <button
                        type="button"
                        data-testid="add-selected-labels"
                        disabled={labelSel.size === 0}
                        onClick={addSelectedLabels}
                        className="bg-emerald-600 disabled:opacity-40 text-white text-xs font-semibold px-3 py-2 rounded whitespace-nowrap"
                      >
                        {t("add_selected")} ({labelSel.size})
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                      {availableLabels.length === 0 && (
                        <p className="text-[11px] text-amber-700 py-2">{t("no_available_labels")}</p>
                      )}
                      {availableLabelsFiltered.map(l => (
                          <label key={l.id} data-testid={`label-opt-${l.codigo}`} className="flex items-center gap-3 py-2 cursor-pointer text-xs">
                            <input
                              type="checkbox"
                              checked={labelSel.has(l.codigo)}
                              onChange={()=>setLabelSel(prev => { const n = new Set(prev); n.has(l.codigo) ? n.delete(l.codigo) : n.add(l.codigo); return n; })}
                            />
                            <span className="font-mono font-semibold text-slate-900">{l.codigo}</span>
                            <span className="text-slate-500 truncate flex-1">{l.receptor_nombre || "—"} · {l.contenido || "—"}</span>
                            <span className="tabular-nums text-slate-600">{l.peso_total || 0} kg</span>
                          </label>
                        ))}
                    </div>
                  </div>
                )}

                {form.items.length === 0 && (
                  <p className="text-[11px] text-slate-500 py-2 italic">— {t("no_items_single_box")} —</p>
                )}
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={it.code || `new-${idx}`} className="bg-white border border-blue-200 rounded-md p-2 grid grid-cols-1 md:grid-cols-[24px_150px_1fr_80px_80px_50px_28px] gap-2 items-center" data-testid={`item-${idx}`}>
                      <div className="w-6 h-6 rounded bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</div>
                      <input
                        placeholder={t("box_code")}
                        value={it.code || ""}
                        onChange={(e)=>setForm(f => { const arr=[...f.items]; arr[idx]={...arr[idx], code: e.target.value}; return {...f, items: arr}; })}
                        data-testid={`item-${idx}-code`}
                        className="input font-mono text-xs"
                      />
                      <input
                        placeholder={t("description")}
                        value={it.descripcion}
                        onChange={(e)=>setForm(f => { const arr=[...f.items]; arr[idx]={...arr[idx], descripcion: e.target.value}; return {...f, items: arr}; })}
                        data-testid={`item-${idx}-desc`}
                        className="input"
                      />
                      <input
                        type="number" step="0.1" placeholder={t("weight_kg")}
                        value={it.peso}
                        onChange={(e)=>setForm(f => { const arr=[...f.items]; arr[idx]={...arr[idx], peso: +e.target.value}; return {...f, items: arr}; })}
                        data-testid={`item-${idx}-peso`}
                        className="input tabular-nums"
                      />
                      <input
                        type="number" step="0.01" placeholder="CBM"
                        value={it.cbm}
                        onChange={(e)=>setForm(f => { const arr=[...f.items]; arr[idx]={...arr[idx], cbm: +e.target.value}; return {...f, items: arr}; })}
                        data-testid={`item-${idx}-cbm`}
                        className="input tabular-nums"
                      />
                      <input
                        type="number" min="1" placeholder="×"
                        value={it.bultos || 1}
                        onChange={(e)=>setForm(f => { const arr=[...f.items]; arr[idx]={...arr[idx], bultos: Math.max(1, +e.target.value || 1)}; return {...f, items: arr}; })}
                        data-testid={`item-${idx}-bultos`}
                        className="input tabular-nums text-center"
                        title={t("bultos")}
                      />
                      <button
                        type="button"
                        onClick={()=>setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                        data-testid={`item-${idx}-remove`}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title={t("delete")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {form.items.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-200 flex items-center justify-end gap-4 text-xs text-blue-900">
                    <span>Σ {t("weight_kg")}: <b className="tabular-nums">{itemTotals.peso} kg</b></span>
                    <span>Σ CBM: <b className="tabular-nums">{itemTotals.cbm}</b></span>
                    <span>Σ {t("bultos")}: <b className="tabular-nums">{itemTotals.bultos}</b></span>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="sf-submit" className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
          <style>{`.input{width:100%;padding:.55rem .75rem;border:1px solid #cbd5e1;border-radius:.5rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.15)}`}</style>
        </div>
      )}

      {auditFor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" data-testid="audit-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-display font-semibold text-lg flex items-center gap-2"><History className="w-5 h-5 text-purple-600" /> {t("audit_trail")}</h3>
                <p className="text-sm text-slate-500 font-mono">{auditFor.tracking_code}</p>
              </div>
              <button onClick={() => { setAuditFor(null); setAuditData(null); }}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {!auditData ? (
              <div className="p-8 text-center text-slate-400 text-sm">…</div>
            ) : (
              <div className="space-y-4">
                {/* Observations on the master + each box */}
                <PackageNotes code={auditFor.tracking_code} label={`${auditFor.tracking_code} (master)`} />
                {(auditFor.items || []).map(it => (
                  <PackageNotes key={it.code} code={it.code} label={it.code} />
                ))}
                {/* Shipment audit log */}
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-600 tracking-wider mb-2">{t("audit_trail")} · shipment</h4>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {(auditData.audit_log || []).length === 0 && <div className="p-3 text-xs text-slate-400 italic">—</div>}
                    {(auditData.audit_log || []).map((h, i) => (
                      <div key={h.id || `${h.at}-${h.action}-${i}`} className="p-3 text-xs flex flex-wrap gap-2 items-center">
                        <span className="font-mono text-[10px] text-slate-500">{new Date(h.at).toLocaleString()}</span>
                        <span className="font-semibold text-purple-700">{h.action}</span>
                        <span className="text-slate-700">{h.user_name || h.user_id?.slice(0, 8)}</span>
                        {h.role && <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{h.role}</span>}
                        {h.warehouse_id && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">wh:{h.warehouse_id?.slice(0, 6)}</span>}
                        {h.manifest_id && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">manifest</span>}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Global audit (from audit_logs) */}
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-600 tracking-wider mb-2">{t("global_audit")}</h4>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
                    {(auditData.global_audit || []).length === 0 && <div className="p-3 text-xs text-slate-400 italic">—</div>}
                    {(auditData.global_audit || []).map((h, i) => (
                      <div key={h.id || `${h.at}-${h.action}-${i}`} className="p-2 text-xs flex flex-wrap gap-2 items-center">
                        <span className="font-mono text-[10px] text-slate-500">{new Date(h.at).toLocaleString()}</span>
                        <span className="font-semibold text-purple-700">{h.action}</span>
                        <span className="text-slate-700">{h.user_name}</span>
                        {h.role && <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{h.role}</span>}
                        {h.notes && <span className="text-slate-500">· {h.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Public history */}
                <div>
                  <h4 className="text-xs font-bold uppercase text-slate-600 tracking-wider mb-2">{t("public_tracking")} · historial</h4>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {(auditData.historial || []).map((h, i) => (
                      <div key={`${h.fecha}-${h.estado}-${i}`} className="p-2 text-xs flex gap-2">
                        <span className="font-mono text-[10px] text-slate-500">{new Date(h.fecha).toLocaleString()}</span>
                        <span className="font-semibold uppercase text-slate-700">{h.estado}</span>
                        {h.nota && <span className="text-slate-500">· {h.nota}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign to Voyage modal */}
      {assignOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="assign-voyage-modal">
          <form onSubmit={submitAssign} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-indigo-600" /> {t("assign_to_voyage")}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {selected.size} {t("selected_shipments")} · {t("manifest_will_be_generated")}
                </p>
              </div>
              <button type="button" onClick={()=>setAssignOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 max-h-32 overflow-y-auto">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("selected_shipments")}</p>
              <ul className="text-xs space-y-1">
                {selectedRows.map(r => (
                  <li key={r.id} className="flex items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-slate-800">{r.tracking_code}</span>
                    <span className="text-slate-500 truncate">{r.origen} → {r.destino}</span>
                    <button type="button" onClick={()=>toggleSelect(r.id)} className="text-red-500 hover:bg-red-50 rounded p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("voyage")}</label>
              <select
                data-testid="assign-voyage-sel"
                value={assignForm.voyage_id}
                onChange={(e)=>pickVoyage(e.target.value)}
                className="input"
              >
                <option value="">— {t("no_voyage_optional")} —</option>
                {voyages.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.codigo || v.nombre} · {v.puerto_origen || "-"} → {v.puerto_destino || "-"} {v.salida ? `· ${v.salida}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("manifest_name")}</label>
                <input required data-testid="assign-name" value={assignForm.nombre} onChange={(e)=>setAssignForm({...assignForm, nombre: e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("mode")}</label>
                <select value={assignForm.modo} onChange={(e)=>setAssignForm({...assignForm, modo: e.target.value})} className="input">
                  <option value="terrestre">{t("terrestre")}</option>
                  <option value="aereo">{t("aereo")}</option>
                  <option value="maritimo">{t("maritimo")}</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("destination")}</label>
                <input data-testid="assign-destino" value={assignForm.destino} onChange={(e)=>setAssignForm({...assignForm, destino: e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("container")}</label>
                <input value={assignForm.container_number} onChange={(e)=>setAssignForm({...assignForm, container_number: e.target.value})} className="input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("vehicle")}</label>
                <input value={assignForm.vehiculo} onChange={(e)=>setAssignForm({...assignForm, vehiculo: e.target.value})} className="input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("driver")}</label>
                <input value={assignForm.conductor} onChange={(e)=>setAssignForm({...assignForm, conductor: e.target.value})} className="input" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("internal_notes")}</label>
              <textarea rows="2" value={assignForm.notas} onChange={(e)=>setAssignForm({...assignForm, notas: e.target.value})} className="input" />
            </div>

            <div className="flex gap-3 pt-2 items-center">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">PDF</span>
                <PdfLangSelect value={assignForm.report_lang} onChange={(v)=>setAssignForm({...assignForm, report_lang: v})} testid="assign-report-lang" />
              </div>
              <button type="button" onClick={()=>setAssignOpen(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-50">{t("cancel")}</button>
              <button type="submit" disabled={assignBusy} data-testid="assign-submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                <Send className="w-4 h-4" /> {assignBusy ? "..." : `${t("assign_and_generate")} (${selected.size})`}
              </button>
            </div>
          </form>
          <style>{`.input{width:100%;padding:.55rem .75rem;border:1px solid #cbd5e1;border-radius:.5rem;font-size:.875rem;outline:none;background:white}.input:focus{border-color:#4f46e5;box-shadow:0 0 0 3px rgba(79,70,229,.15)}`}</style>
        </div>
      )}
    </div>
  );
}

function AddressCard({ side, addr, t }) {
  const isOrigin = side === "origen";
  const line1 = [addr.calle, addr.numero].filter(Boolean).join(" ");
  const line2 = [addr.codigo_postal, addr.ciudad].filter(Boolean).join(" · ");
  const line3 = [addr.provincia, addr.pais].filter(Boolean).join(", ");
  const phone = [addr.telefono_prefijo, addr.telefono].filter(Boolean).join(" ");
  return (
    <div className={`rounded-lg border p-3 ${isOrigin ? "border-blue-200 bg-white" : "border-red-200 bg-white"}`} data-testid={`addrcard-${side}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded text-white ${isOrigin ? "bg-blue-600" : "bg-red-600"}`}>
          {isOrigin ? t("sender") : t("receiver")}
        </span>
        {addr.pais && <span className="text-[10px] font-semibold text-slate-500 uppercase">{addr.pais}</span>}
      </div>
      <div className="text-sm font-semibold text-slate-900">{addr.nombre_completo || "—"}</div>
      {addr.empresa && <div className="text-xs text-slate-600">{addr.empresa}</div>}
      <div className="text-xs text-slate-600 mt-1 space-y-0.5">
        {line1 && <div>{line1}</div>}
        {line2 && <div>{line2}</div>}
        {line3 && <div>{line3}</div>}
        {phone && <div className="tabular-nums">📞 {phone}</div>}
        {addr.email && <div className="truncate">{addr.email}</div>}
        {addr.tax_id && <div className="text-[10px] text-slate-500 mt-1">Tax ID: {addr.tax_id}</div>}
      </div>
    </div>
  );
}


function AddressBlock({ side, addr, onChange, t, countries = [], lang = "es" }) {
  const set = (k, v) => onChange({ ...addr, [k]: v });
  const title = side === "origen" ? t("sender") : t("receiver");
  const accent = side === "origen" ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200";
  const accentTag = side === "origen" ? "bg-blue-600" : "bg-red-600";
  const testPrefix = side === "origen" ? "sf-o" : "sf-d";
  return (
    <div className={`border-2 rounded-xl p-4 space-y-3 ${accent}`} data-testid={`${side}-block`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`${accentTag} text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded`}>{side === "origen" ? t("origin_country") : t("destination_country")}</span>
          <h4 className="font-semibold text-sm text-slate-800">{title}</h4>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2.5">
        <div>
          <label className="lbl">{t("fullname")} <span className="text-red-500">*</span></label>
          <input required data-testid={`${testPrefix}-name`} value={addr.nombre_completo} onChange={(e)=>set("nombre_completo", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("company")}</label>
          <input data-testid={`${testPrefix}-company`} value={addr.empresa} onChange={(e)=>set("empresa", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("email")}</label>
          <input type="email" data-testid={`${testPrefix}-email`} value={addr.email} onChange={(e)=>set("email", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("phone")} <span className="text-red-500">*</span></label>
          <div className="flex gap-1">
            <input placeholder="+34" data-testid={`${testPrefix}-phone-prefix`} value={addr.telefono_prefijo} onChange={(e)=>set("telefono_prefijo", e.target.value)} className="input w-16 text-center" />
            <input required data-testid={`${testPrefix}-phone`} value={addr.telefono} onChange={(e)=>set("telefono", e.target.value)} className="input flex-1" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="lbl">{t("country")} <span className="text-red-500">*</span></label>
          <CountrySelect
            countries={countries}
            value={addr.pais}
            onChange={(v) => onChange({ ...addr, pais: v, provincia: "" })}
            testid={`${testPrefix}-country`}
            required
          />
        </div>
        <div className="sm:col-span-2">
          <label className="lbl">{t("street")} <span className="text-red-500">*</span></label>
          <input required data-testid={`${testPrefix}-street`} value={addr.calle} onChange={(e)=>set("calle", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("number")}</label>
          <input data-testid={`${testPrefix}-number`} value={addr.numero} onChange={(e)=>set("numero", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("postal_code")} <span className="text-red-500">*</span></label>
          <input required data-testid={`${testPrefix}-postal`} value={addr.codigo_postal} onChange={(e)=>set("codigo_postal", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("city")} <span className="text-red-500">*</span></label>
          <input required data-testid={`${testPrefix}-city`} value={addr.ciudad} onChange={(e)=>set("ciudad", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("province")} <span className="text-red-500">*</span></label>
          <ProvinceSelect
            countries={countries}
            country={addr.pais}
            value={addr.provincia}
            onChange={(v) => set("provincia", v)}
            testid={`${testPrefix}-province`}
            required
          />
        </div>
        <div>
          <label className="lbl">{t("tax_id")}</label>
          <input data-testid={`${testPrefix}-taxid`} value={addr.tax_id} onChange={(e)=>set("tax_id", e.target.value)} className="input" />
        </div>
        <div>
          <label className="lbl">{t("alias")}</label>
          <input data-testid={`${testPrefix}-alias`} value={addr.alias} onChange={(e)=>set("alias", e.target.value)} className="input" />
        </div>
        <div className="sm:col-span-2">
          <label className="lbl">{t("address_reference")}</label>
          <input data-testid={`${testPrefix}-ref`} value={addr.referencia} onChange={(e)=>set("referencia", e.target.value)} className="input" />
        </div>
      </div>
      <style>{`.lbl{display:block;font-size:11px;font-weight:600;color:#475569;margin-bottom:2px;text-transform:none}`}</style>
    </div>
  );
}



/* =========================================================================
   ClientLabelsSection — a client's registered packages (labels) with
   tracking button that opens the public track page for full traceability.
   ========================================================================= */
function ClientLabelsSection({ labels, t, search, nav }) {
  const q = (search || "").trim().toLowerCase();
  const filtered = q
    ? labels.filter(l => (
        [l.codigo, l.tracking_transportista, l.transportista, l.destino_ciudad,
         l.destino_pais, l.referencia, l.master_code, l.office_exit_code]
          .filter(Boolean).join(" ").toLowerCase().includes(q)
      ))
    : labels;
  if (filtered.length === 0) return null;
  const flowTone = {
    clasificado: "bg-amber-100 text-amber-700",
    en_salida: "bg-indigo-100 text-indigo-700",
    transito_hub: "bg-blue-100 text-blue-700",
    recibido_hub: "bg-violet-100 text-violet-700",
    verificado_hub: "bg-emerald-100 text-emerald-700",
    en_master: "bg-teal-100 text-teal-700",
    master_cerrado: "bg-slate-800 text-white",
    entregado: "bg-emerald-600 text-white",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="client-labels-section">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Tag className="w-4 h-4 text-red-600" /> {t("my_registered_packages") || "Mis paquetes registrados"}
          </h3>
          <p className="text-xs text-slate-500">{filtered.length} {(t("packages_count") || "paquetes").toLowerCase()}</p>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {filtered.map(l => {
          const flow = l.estado_flujo || "clasificado";
          return (
            <li key={l.id} data-testid={`client-lbl-${l.codigo}`} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-slate-900 text-sm">{l.codigo}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${flowTone[flow] || "bg-slate-100 text-slate-700"}`}>
                    {t(`flow_${flow}`) || flow}
                  </span>
                  {l.master_code && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
                      MASTER · {l.master_code}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  {[l.origen_ciudad, l.origen_pais].filter(Boolean).join(", ") || "—"}
                  <ArrowRight className="w-3 h-3 text-slate-400" />
                  {[l.destino_ciudad, l.destino_pais].filter(Boolean).join(", ") || "—"}
                  <span className="text-slate-400 ms-2">· {(l.numero_paquetes || 1)} {(t("packages_count") || "paquetes").toLowerCase()} · {(l.peso_total || 0)} kg</span>
                </div>
              </div>
              <button
                data-testid={`client-lbl-track-${l.codigo}`}
                onClick={() => nav(`/track/${encodeURIComponent(l.codigo)}`)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5"
              >
                <Search className="w-3.5 h-3.5" /> {t("track") || "Rastrear"}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
